import axios from 'axios';
import { geoIpLookup } from './geo-location';

const translations = {};
const instances = new Map();

// The WhatsApp check running for each phone input, so a newer edit can cancel the
// reply for the number it replaced. Keyed by the input element, like `instances`.
const checksInFlight = new Map();

function loadTranslations() {
    const url = window.whatsappTranslationsUrl || '/whatsapp/translations';
    return axios.get(url)
        .then(response => {
            Object.assign(translations, response.data);
        })
        .catch(error => {
            console.error('Failed to load translations:', error);
        });
}

export function initIntlTelInput(selector, hiddenSelector, hiddenValidationResult = null) {
    loadTranslations().then(function () {
        const utilsScriptUrl = 'https://cdn.jsdelivr.net/npm/intl-tel-input@24.8.2/build/js/utils.js';
        const preferredCountriesList = ['ar', 'au', 'br', 'ca', 'cl', 'co', 'es', 'ec', 'mx', 'pe'];
        const phoneInputs = document.querySelectorAll(selector);
        const hiddenInput = document.querySelector(hiddenSelector);
        const whatsappValidationUrl = window.whatsappValidationUrl || '/whatsapp/validate';

        if (!phoneInputs.length) {
            console.error(`IntlTelInput: No elements found for selector '${selector}'`);
            return;
        }

        const options = {
            separateDialCode: true,
            initialCountry: 'auto',
            countryOrder: preferredCountriesList,
            loadUtilsOnInit: utilsScriptUrl,
            geoIpLookup: geoIpLookup
        };

        phoneInputs.forEach(input => {
            if (instances.has(input)) {
                return;
            }

            const instance = window.intlTelInput(input, options);

            instances.set(input, instance);
            instance.promise.then(function () {
                createValidationElements(input);
                attachEventListeners(input, instance);
            }).catch(function (error) {
                console.error('IntlTelInput failed to load', error);
            });
        });

        function createValidationElements(input) {
            const grandparent = input.parentNode.parentNode;
            const loader = document.createElement('div');
            const wrapper = document.createElement('div');
            const validated = document.createElement('div');

            wrapper.classList.add('phone-validation-container');
            wrapper.innerHTML = `
                <p id="error-msg" class="hide help is-invalid"></p>
                <p id="whatsapp-status" class="hide help wa-status"></p>
            `;

            grandparent.insertBefore(wrapper, input.parentNode.nextSibling);

            loader.classList.add('validation-loading', 'hide');
            loader.innerHTML = `
                <span class="loading-text">is checking...</span>
                <img src="/images/spinner-red.png" class="loading-icon" alt="Checking phone number">
            `;

            validated.classList.add('validation-complete', 'hide');
            input.parentNode.insertBefore(loader, input.nextSibling);
            input.parentNode.insertBefore(validated, loader.nextSibling);
            input.validationWrapper = {
                wrapper: wrapper,
                errorMsg: wrapper.querySelector('#error-msg'),
                whatsappStatus: wrapper.querySelector('#whatsapp-status'),
                loader: loader,
                validated: validated
            };
        }

        function restoreOldPhoneNumber(input, instance) {
            if (!hiddenInput.value) {
                return;
            }

            let storedNumber = hiddenInput.value;
            let isStartingWithZero = storedNumber.startsWith('0');

            if (!storedNumber.startsWith('+') && !isStartingWithZero) {
                storedNumber = '+' + storedNumber;
            }

            instance.setNumber(storedNumber);

            if (isStartingWithZero) {
                instance.setCountry('au');
            }
        }

        function attachEventListeners(input, instance) {
            if (hiddenInput.value) {
                restoreOldPhoneNumber(input, instance);
            }

            // Initialize the hidden field immediately if input has value
            if (input.value.trim()) {
                validateInput(input, instance);
                updateHiddenInput(input, instance);
                console.log('Inter phone input initialized with existing value.');

                // restoreOldPhoneNumber may have rewritten the stored number into a
                // different shape. Forms comparing against it need to re-read it now,
                // or a number nobody touched compares as changed.
                input.dispatchEvent(new CustomEvent('phoneNumberRestored', { bubbles: true }));
            }

            ['input', 'countrychange'].forEach(event => {
                input.addEventListener(event, () => {
                    validateInput(input, instance);
                    updateHiddenInput(input, instance);
                });
            });

            // Add a form submission handler to ensure the hidden field is populated
            const closerForm = input.closest('form');
            if (closerForm) {
                closerForm.addEventListener('submit', function(e) {
                    if (input.value.trim() && (!hiddenInput.value || hiddenInput.value.trim() === '')) {
                        validateInput(input, instance);
                        updateHiddenInput(input, instance);
                        console.log('Inter phone input populated on form submission.');
                    }
                });
            }

            // Timeout fallback to ensure the hidden field gets populated
            setTimeout(() => {
                // console.log('Inter phone input timeout fallback.');
                if (input.value.trim() && (!hiddenInput.value || hiddenInput.value.trim() === '')) {
                    validateInput(input, instance);
                    updateHiddenInput(input, instance);
                    console.log('Inter phone input not populated after 100ms. Using fallback.');
                }
            }, 100);
        }

        function updateHiddenInput(input, instance) {
            if (hiddenInput) {
                let fullNumber = instance.getNumber();
                let cleanedNumber = fullNumber.replace(/\+0+/, '+');

                hiddenInput.value = cleanedNumber;
            }
        }

        /** Drop the check running for this input, so a late reply cannot write itself in. */
        function abortCheckInFlight(input) {
            const controller = checksInFlight.get(input);

            if (controller) {
                checksInFlight.delete(input);
                controller.abort();
            }
        }

        /** Axios names an aborted request differently across versions. */
        function wasAborted(error) {
            return error?.code === 'ERR_CANCELED'
                || error?.name === 'CanceledError'
                || error?.name === 'AbortError'
                || (typeof axios.isCancel === 'function' && axios.isCancel(error));
        }

        /**
         * The result input answers for the number currently typed, so it is emptied
         * whenever that number changes — otherwise the previous answer gets posted.
         *
         * Three states, and forms gate on them: 'pending' while a check is actually
         * running, 'true'/'false' once it replied, and '' for every other standing —
         * never checked, check failed, number rejected before a check could start.
         */
        function setValidationResult(value) {
            if (!hiddenValidationResult) {
                return;
            }

            const resultInput = document.querySelector(hiddenValidationResult);

            if (resultInput) {
                resultInput.value = value;
            }
        }

        function validateInput(input, instance) {
            const {errorMsg, whatsappStatus, loader, validated} = input.validationWrapper;
            const errorMap = [
                '',
                translations.phone_invalid_country || 'Invalid country code',
                translations.phone_too_short || 'Too short',
                translations.phone_too_long || 'Too long',
                translations.phone_invalid_number || 'Invalid number',
            ];

            validated.classList.add('hide');
            whatsappStatus.classList.add('hide');
            abortCheckInFlight(input);
            setValidationResult('');

            if (!input.value.trim()) {
                loader.classList.add('hide');
                errorMsg.classList.add('hide');
                errorMsg.innerHTML = '';
                whatsappStatus.classList.add('hide', 'is-invalid');
                whatsappStatus.innerHTML = '';
                input.classList.remove('is-danger', 'error');
                input.dispatchEvent(new CustomEvent('phoneValidationComplete', { bubbles: true }));
                return;
            }

            loader.classList.remove('hide');

            input.value = input.value.replace(/^0+/, '');
            const errorCode = instance.getValidationError();

            if (instance.isValidNumber()) {
                // console.log('Valid phone number');
                input.classList.remove('is-danger');
                errorMsg.classList.add('hide');
                whatsappStatus.classList.remove('hide', 'is-invalid');
                whatsappStatus.innerHTML = translations.checking_whatsapp;
                whatsappStatus.style.color = 'orange';

                return validateWhatsapp(instance.getNumber(), whatsappStatus, validated, loader, input);
            } else {
                // Nothing is being checked, so the spinner has to go with it.
                loader.classList.add('hide');
                input.classList.add('is-danger');
                errorMsg.innerHTML = errorMap[errorCode] || (translations.phone_invalid_number || 'Invalid number');
                errorMsg.classList.remove('hide');
                whatsappStatus.classList.add('hide');
            }
        }

        function validateWhatsapp(number, whatsappStatus, validated, loader, input) {
            if (!whatsappValidationUrl) {
                return Promise.resolve(false);
            }

            const invalidColor = getComputedStyle(input).getPropertyValue('--embed-invalid-color').trim() || '#E3342F';

            // One check in flight per input: replies can land out of order, and the answer
            // for a number already edited away must not be the one stored.
            abortCheckInFlight(input);
            const controller = new AbortController();
            checksInFlight.set(input, controller);
            setValidationResult('pending');

            return new Promise((resolve) => {
                axios.post(whatsappValidationUrl, {
                    number: number,
                    _token: document.querySelector('meta[name="csrf-token"]').getAttribute('content')
                }, { signal: controller.signal }).then(response => {
                    let isValid = response.data.is_valid;

                    setValidationResult(isValid);

                    if (isValid) {
                        whatsappStatus.innerHTML = translations.whatsapp_valid;
                        whatsappStatus.style.color = '#22C55E';
                        whatsappStatus.classList.remove('is-invalid');
                        input.classList.remove('error');
                        validated.classList.remove('hide');
                        validated.innerHTML = '<i class="fab fa-whatsapp"></i>';

                        input.dispatchEvent(new CustomEvent('phoneValidationComplete', { bubbles: true }));
                        resolve(true);
                    } else {
                        whatsappStatus.innerHTML = translations.whatsapp_invalid;
                        whatsappStatus.style.color = invalidColor;
                        whatsappStatus.classList.add('is-invalid');
                        input.classList.add('error');
                        validated.classList.remove('hide');
                        validated.innerHTML = '<i class="fas fa-times"></i>';
                        
                        input.dispatchEvent(new CustomEvent('phoneValidationComplete', { bubbles: true }));
                        resolve(false);
                    }
                }).catch((error) => {
                    // A newer check for this input replaced this one; it owns the UI now.
                    if (wasAborted(error)) {
                        resolve(false);
                        return;
                    }

                    // Nothing was learned, which is the same standing as never having
                    // checked. Leaving 'pending' here would hold the forms forever.
                    setValidationResult('');

                    whatsappStatus.innerHTML = translations.whatsapp_error;
                    whatsappStatus.style.color = invalidColor;
                    whatsappStatus.classList.add('is-invalid');
                    resolve(false);
                }).finally(() => {
                    // Only the current check clears the spinner; a late aborted one would
                    // hide the loader its replacement just showed.
                    if (checksInFlight.get(input) === controller) {
                        checksInFlight.delete(input);
                        loader.classList.add('hide');
                    }
                });
            });
        }

        window.isValidNumber = function(input) {
            const instance = instances.get(input);
            return instance ? instance.isValidNumber() : false;
        };

        window.isValidWhatsappNumber = function(input) {
            const instance = instances.get(input);
            if (!instance || !instance.isValidNumber()) {
                return Promise.resolve(false);
            }

            return validateWhatsapp(instance.getNumber(), input.validationWrapper.whatsappStatus, input.validationWrapper.validated, input.validationWrapper.loader, input);
        };

        return {
            getNumber: function (input) {
                const instance = instances.get(input);
                return instance ? instance.getNumber() : null;
            },
            isValidNumber: function (input) {
                const instance = instances.get(input);
                return instance ? instance.isValidNumber() : false;
            },
            isValidWhatsappNumber: function (input) {
                const instance = instances.get(input);
                if (!instance || !instance.isValidNumber()) {
                    return false;
                }

                return validateWhatsapp(instance.getNumber(), input.validationWrapper.whatsappStatus, input.validationWrapper.validated, input.validationWrapper.loader, input);
            },
            destroy: function () {
                instances.forEach(function (instance, input) {
                    instance.destroy();
                });
                instances.clear();
            }
        };
    });
}

// Check if a phone input is valid and connected to wa
export function isPhoneAndWhatsappValid(input) {
    if (!input || !input.value.trim() || !input.validationWrapper) {
        return false;
    }

    const { loader, errorMsg, whatsappStatus } = input.validationWrapper;

    if (loader && !loader.classList.contains('hide')) {
        return false;
    }

    if (errorMsg && !errorMsg.classList.contains('hide')) {
        return false;
    }

    if (whatsappStatus) {
        const color = whatsappStatus.style.color;
        return color === 'rgb(34, 197, 94)' || color === '#22C55E';
    }

    return false;
}

document.addEventListener('DOMContentLoaded', () => {
    document.addEventListener('keydown', function(e) {
        if (e.target.classList.contains('iti__tel-input')) {
            const allowedKeys = ['+', '-', 'Backspace', 'ArrowLeft', 'ArrowRight', 'Tab'];
            const isNumber = e.key >= '0' && e.key <= '9';

            if (!allowedKeys.includes(e.key) && !isNumber) {
                e.preventDefault();
            }
        }
    });
});

window.initIntlTelInput = initIntlTelInput;
